import { AlpineComponent } from "alpinejs";
import {
	client,
	type HasValidSalesForceAccountErrors,
	type HasValidSalesForceAccountResponses,
	type HasValidUmbracoAccountErrors,
	type HasValidUmbracoAccountResponses,
	type ValidationFailure,
} from "../api";

interface SubmissionError {
	errorMessage: string;
}

interface LoginComponentConfig {
	registerUrl?: string;
	culture?: string;
	requiredEmailText?: string;
	accountExistsText?: string;
	missingAccountText?: string;
	missingAccountWithLinkText?: string;
	verificationFailedText?: string;
	genericErrorText?: string;
	goToRegistrationFormText?: string;
}

interface SalesForceAccountCheckResponse {
	exists: boolean;
	success: boolean;
}

type ApiErrorItem = SubmissionError | ValidationFailure;
type ApiErrors = ApiErrorItem | ApiErrorItem[] | string | string[] | null | undefined;

type LoginComponent = AlpineComponent<{
	isRegistration: boolean;
	registrationComplete: boolean;
	email: string;
	registerUrl: string;
	culture: string;
	showRegisterLink: boolean;
	submissionErrors: SubmissionError[];
	requiredEmailText: string;
	accountExistsText: string;
	missingAccountText: string;
	missingAccountWithLinkText: string;
	verificationFailedText: string;
	genericErrorText: string;
	goToRegistrationFormText: string;
	toggleRegistration: () => void;
	showRegistration: () => void;
	hideRegistration: () => void;
	sendRegistration: (event: SubmitEvent) => void;
	sendRegistrationEmailAgain: () => void;
	sendVerificationEmail: () => Promise<boolean>;
	resetRegistrationState: (clearEmail?: boolean) => void;
	addErrors: (errors: ApiErrors) => void;
	buildRegisterUrl: () => string;
}>;

export default (config: string | LoginComponentConfig = ""): LoginComponent => {
	const resolvedConfig = typeof config === "string" ? { registerUrl: config } : config;

	return {
		isRegistration: false,
		registrationComplete: false,
		email: "",
		registerUrl: resolvedConfig.registerUrl ?? "",
		culture: resolvedConfig.culture ?? "",
		showRegisterLink: false,
		submissionErrors: [],
		requiredEmailText: resolvedConfig.requiredEmailText ?? "Please enter an email address.",
		accountExistsText:
			resolvedConfig.accountExistsText ??
			"An account already exists for this email address.",
		missingAccountText:
			resolvedConfig.missingAccountText ??
			"We could not find a customer with this email address.",
		missingAccountWithLinkText:
			resolvedConfig.missingAccountWithLinkText ??
			"We could not find a customer with this email address. Continue to the registration form.",
		verificationFailedText:
			resolvedConfig.verificationFailedText ??
			"Could not send the verification email. Please try again later.",
		genericErrorText:
			resolvedConfig.genericErrorText ?? "Something went wrong. Please try again later.",
		goToRegistrationFormText:
			resolvedConfig.goToRegistrationFormText ?? "Go to registration form",
		toggleRegistration() {
			this.isRegistration = !this.isRegistration;
			this.resetRegistrationState();
		},
		showRegistration() {
			this.isRegistration = true;
			this.resetRegistrationState();
		},
		hideRegistration() {
			this.isRegistration = false;
			this.resetRegistrationState();
		},
		async sendRegistration(event: SubmitEvent) {
			event.preventDefault();
			this.resetRegistrationState(false);

			if (!this.email) {
				this.submissionErrors.push({ errorMessage: this.requiredEmailText });
				return;
			}

			const umbracoCheck = await client.get<
				HasValidUmbracoAccountResponses,
				HasValidUmbracoAccountErrors
			>({
				url: "/_esfn/api/Account/hasvalidumbracoaccount",
				query: { email: this.email },
			});

			if (!umbracoCheck.response.ok) {
				this.addErrors(umbracoCheck.error);
				return;
			}

			if (umbracoCheck.data?.exists) {
				this.submissionErrors.push({
					errorMessage: this.accountExistsText,
				});
				return;
			}

			const salesforceCheck = await client.get<
				HasValidSalesForceAccountResponses,
				HasValidSalesForceAccountErrors
			>({
				url: "/_esfn/api/Account/hasvalidsalesforceaccount",
				query: { email: this.email },
			});

			if (!salesforceCheck.response.ok) {
				this.addErrors(salesforceCheck.error);
				return;
			}

			if (!salesforceCheck.data?.exists) {
				this.showRegisterLink = !!this.registerUrl;
				if (this.showRegisterLink) {
					window.location.href = this.buildRegisterUrl();
					return;
				}
				this.submissionErrors.push({
					errorMessage: this.showRegisterLink
						? this.missingAccountWithLinkText
						: this.missingAccountText,
				});
				return;
			}

			const sent = await this.sendVerificationEmail();
			if (sent) {
				this.registrationComplete = true;
			}
		},
		async sendRegistrationEmailAgain() {
			this.submissionErrors = [];
			this.showRegisterLink = false;

			if (!this.email) {
				this.submissionErrors.push({ errorMessage: this.requiredEmailText });
				return;
			}

			const salesforceCheck = await client.get<
				HasValidSalesForceAccountResponses,
				HasValidSalesForceAccountErrors
			>({
				url: "/_esfn/api/Account/hasvalidsalesforceaccount",
				query: { email: this.email },
			});

			if (!salesforceCheck.response.ok) {
				this.addErrors(salesforceCheck.error);
				return;
			}

			if (!salesforceCheck.data?.exists) {
				this.showRegisterLink = !!this.registerUrl;
				if (this.showRegisterLink) {
					window.location.href = this.buildRegisterUrl();
					return;
				}
				this.submissionErrors.push({
					errorMessage: this.showRegisterLink
						? this.missingAccountWithLinkText
						: this.missingAccountText,
				});
				return;
			}

			await this.sendVerificationEmail();
		},
		async sendVerificationEmail() {
			const culture = this.culture || document.documentElement.lang || undefined;
			const verification = await client.post<
				{ 200: SalesForceAccountCheckResponse },
				{ 400: ValidationFailure[] }
			>({
				url: "/_esfn/api/Account/triggeremailverification",
				headers: {
					"Content-Type": "application/json",
				},
				body: {
					email: this.email,
					culture,
				},
			});

			if (!verification.response.ok) {
				this.addErrors(verification.error);
				return false;
			}

			if (!verification.data?.success) {
				this.submissionErrors.push({
					errorMessage: this.verificationFailedText,
				});
				return false;
			}

			return true;
		},
		resetRegistrationState(clearEmail = true) {
			this.registrationComplete = false;
			this.submissionErrors = [];
			this.showRegisterLink = false;
			if (clearEmail) {
				this.email = "";
			}
		},
		addErrors(errors: ApiErrors) {
			if (!errors || (Array.isArray(errors) && errors.length === 0)) {
				this.submissionErrors.push({ errorMessage: this.genericErrorText });
				return;
			}

			const normalized = Array.isArray(errors) ? errors : [errors];

			for (const error of normalized) {
				if (typeof error === "string") {
					this.submissionErrors.push({ errorMessage: error });
					continue;
				}

				if (error?.errorMessage) {
					this.submissionErrors.push({ errorMessage: error.errorMessage });
				} else {
					this.submissionErrors.push({ errorMessage: this.genericErrorText });
				}
			}
		},
		buildRegisterUrl() {
			if (!this.registerUrl) {
				return "";
			}

			try {
				const url = new URL(this.registerUrl, window.location.origin);
				if (this.email) {
					url.searchParams.set("email", this.email);
				}
				return url.toString();
			} catch {
				if (!this.email) {
					return this.registerUrl;
				}
				const separator = this.registerUrl.includes("?") ? "&" : "?";
				return `${this.registerUrl}${separator}email=${encodeURIComponent(this.email)}`;
			}
		},
	};
};
